import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import UserLayout from './UserLayout';
import PageHeader from '../shared/PageHeader';
import Card from '../shared/Card';
import Button from '../shared/Button';
import { SkeletonRows } from '../shared/Skeleton';
import { sealLookupApi } from '../../services/sealLookupApi';

// Seal Lookup: behaviour per b2b-portal-seal-lookup.md. All data goes through sealLookupApi.

const ROLES = ['inner', 'outer', 'height'];
const ROLE_NAMES = { inner: 'ID', outer: 'OD', height: 'height' };
const PAGE_SIZE = 50;
const ENUMS = { mode: ['seal', 'housing'], app: ['any', 'rod', 'piston'], units: ['mm', 'in'], system: ['both', 'metric', 'imperial'] };
const DEFAULTS = {
  mode: 'seal', app: 'any', units: 'mm', system: 'both', q: '',
  inner: '', innerTol: '', outer: '', outerTol: '', height: '', heightTol: '',
  dim: {}, dimTol: {}, types: [], profiles: [], multi: false, page: 1, includeIncomplete: false,
};

const LABEL = 'mb-1 block text-[13px] font-semibold text-gray-700';
const INPUT = 'rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent';
const TH = 'px-2 py-1.5 font-semibold';
const TD = 'px-2 py-1.5';

function readUrl(search, meta) {
  const p = new URLSearchParams(search);
  const s = { ...DEFAULTS, dim: {}, dimTol: {} };
  Object.entries(ENUMS).forEach(([k, allowed]) => { s[k] = allowed.includes(p.get(k)) ? p.get(k) : allowed[0]; });
  ['q', ...ROLES, ...ROLES.map((r) => `${r}Tol`)].forEach((k) => { s[k] = p.get(k) ?? ''; });
  for (const [name, value] of p.entries()) {
    const m = name.match(/^(dim|dimTol)\[(.+)\]$/);
    if (m) s[m[1]][m[2]] = value;
  }
  const known = new Set(meta.sealTypes.map((t) => t.key));
  s.types = p.getAll('types[]').filter((t) => known.has(t));
  s.profiles = p.getAll('profiles[]').filter((id) => /^\d+$/.test(id)).map(Number);
  s.page = Math.max(1, parseInt(p.get('page'), 10) || 1);
  s.includeIncomplete = p.get('includeIncomplete') === '1';
  s.multi = p.get('multi') === '1' || s.types.length > 1;
  return s;
}

function toParams(s) {
  const p = new URLSearchParams({ mode: s.mode });
  if (s.mode === 'housing') p.set('app', s.app);
  p.set('units', s.units);
  p.set('system', s.system);
  if (s.q.trim()) p.set('q', s.q.trim());
  const add = (name, tolName, value, tol) => {
    if (!value?.trim()) return;
    p.set(name, value.trim());
    if (tol?.trim()) p.set(tolName, tol.trim());
  };
  ROLES.forEach((r) => add(r, `${r}Tol`, s[r], s[`${r}Tol`]));
  Object.keys(s.dim).forEach((k) => add(`dim[${k}]`, `dimTol[${k}]`, s.dim[k], s.dimTol[k]));
  s.types.forEach((t) => p.append('types[]', t));
  s.profiles.forEach((id) => p.append('profiles[]', id));
  if (s.page > 1) p.set('page', s.page);
  if (s.includeIncomplete) p.set('includeIncomplete', '1');
  return p;
}

const hasSizes = (s) => ROLES.some((r) => s[r].trim()) || Object.values(s.dim).some((v) => v.trim());
const hasTolerance = (s) => ROLES.some((r) => s[`${r}Tol`].trim()) || Object.values(s.dimTol).some((v) => v.trim());

/** The selected profiles' length fields; else, with exactly one type, all of its profiles' fields. */
function moreDimsFor(meta, types, profiles) {
  const source = profiles.length
    ? meta.profiles.filter((p) => profiles.includes(p.id))
    : types.length === 1 ? meta.profiles.filter((p) => p.type === types[0]) : [];
  const out = [];
  source.forEach((p) => p.lengthKeys.forEach((k) => { if (!out.some((o) => o.key === k.key)) out.push(k); }));
  return out;
}

/** Apply a type/profile selection, dropping profiles and More-dimensions values that no longer apply. */
function selectFor(meta, base, types, profiles) {
  const kept = profiles.filter((id) => types.includes(meta.profiles.find((p) => p.id === id)?.type));
  const keys = new Set(moreDimsFor(meta, types, kept).map((d) => d.key));
  const keep = (obj) => Object.fromEntries(Object.entries(obj).filter(([k]) => keys.has(k)));
  return { ...base, types, profiles: kept, dim: keep(base.dim), dimTol: keep(base.dimTol) };
}

function labelsFor(meta, s) {
  const key = s.mode === 'seal' ? 'seal' : `housing_${s.app}`;
  const one = s.types.length === 1 && meta.sealTypes.find((t) => t.key === s.types[0]);
  return (one ? one.labels : meta.genericLabels)[key];
}

const MIXED = 'mixed';

/** Search by / Type dropdown values for a type selection: '' is All, MIXED is a tile selection they can't express. */
function dropdownsFor(meta, types) {
  const families = [...new Set(meta.sealTypes.filter((t) => types.includes(t.key)).map((t) => t.family))];
  const family = families.length > 1 ? MIXED : families[0] ?? '';
  const inFamily = meta.sealTypes.filter((t) => t.family === family);
  const type = types.length === 1 ? types[0] : types.length && types.length !== inFamily.length ? MIXED : '';
  return { family, type, inFamily };
}

const fmt = (v, unit) => String(Number(Number(v).toFixed(unit === 'in' ? 4 : 3)));
const span = (min, max, unit) => `${min === max ? fmt(min, unit) : `${fmt(min, unit)}–${fmt(max, unit)}`}${unit ? ` ${unit}` : ''}`;

function sizeText(m, units) {
  let text = span(m.min, m.max, m.unit);
  if (m.unit && m.unit !== units) {
    const f = units === 'in' ? 25.4 : 1;
    text += ` (${span(m.mmMin / f, m.mmMax / f, units)})`;
  }
  return (m.approx ? '≈ ' : '') + text;
}

function dimText(d) {
  let text;
  if (typeof d.value === 'boolean') text = d.value ? 'yes' : 'no';
  else if (d.min != null && d.max != null) text = span(d.min, d.max, d.unit);
  else if (typeof d.value === 'number') text = span(d.value, d.value, d.unit);
  else text = d.value ?? '—';
  if (d.tolUpper != null || d.tolLower != null) text += ` (+${fmt(d.tolUpper ?? 0, d.unit)} / ${fmt(d.tolLower ?? 0, d.unit)})`;
  return text;
}

const initials = (name) => name.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

function LinkButton({ children, ...props }) {
  return (
    <button
      type="button"
      className="rounded font-medium text-gray-900 underline underline-offset-2 hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      {...props}
    >
      {children}
    </button>
  );
}

function Warn({ children }) {
  return <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{children}</div>;
}

function Segmented({ label, name, value, options, onChange }) {
  return (
    <fieldset>
      <legend className={LABEL}>{label}</legend>
      <div className="inline-flex overflow-hidden rounded-lg border border-gray-300 text-sm">
        {options.map(([v, text], i) => (
          <label
            key={v}
            className={`cursor-pointer px-3 py-1.5 font-medium has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-brand
              ${i ? 'border-l border-gray-300' : ''}
              ${value === v ? 'bg-brand-dark text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            <input type="radio" className="sr-only" name={name} value={v} checked={value === v} onChange={() => onChange(v)} />
            {text}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function Select({ id, label, value, options, onChange }) {
  return (
    <div>
      <label htmlFor={id} className={LABEL}>{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={INPUT}>
        {options.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
      </select>
    </div>
  );
}

function SizeField({ id, label, value, tol, error, onValue, onTol }) {
  return (
    <div>
      <label htmlFor={id} className={LABEL}>{label}</label>
      <div className="flex items-center gap-1.5">
        <input
          id={id}
          className={`${INPUT} w-[110px]`}
          value={value}
          onChange={(e) => onValue(e.target.value)}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <span aria-hidden="true" className="text-gray-500">±</span>
        <input
          className={`${INPUT} w-[90px]`}
          inputMode="decimal"
          placeholder="0.1 or 2%"
          aria-label={`${label} tolerance`}
          value={tol}
          onChange={(e) => onTol(e.target.value)}
        />
      </div>
      {error && <p id={`${id}-error`} className="mt-1 text-xs text-red-700">{error}</p>}
    </div>
  );
}

function SizeLines({ hit, query }) {
  const lines = ROLES.filter((r) => hit.matched[r]).map((r) => (
    <div key={r} className="whitespace-nowrap">
      <span className="text-gray-500">{hit.matched[r].title}: </span>{sizeText(hit.matched[r], query.units)}
    </div>
  ));
  const missing = query.includeIncomplete ? ROLES.filter((r) => query[r].trim() && !hit.matched[r]) : [];
  missing.forEach((r) => lines.push(
    <div key={`missing-${r}`} className="italic text-amber-700">no {ROLE_NAMES[r]} in catalogue</div>
  ));
  return lines.length ? lines : '—';
}

function SealLookup() {
  const history = useHistory();
  const initialSearch = useRef(useLocation().search);
  const [meta, setMeta] = useState(null);
  const [metaFailed, setMetaFailed] = useState(false);
  const [s, setS] = useState(DEFAULTS);
  const [result, setResult] = useState(null); // { res, query }: the last successful search and what it was for
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [openRows, setOpenRows] = useState({});
  const [dimsOpen, setDimsOpen] = useState(false);
  const requestId = useRef(0);
  const resultsRef = useRef(null);

  const set = (patch) => setS((prev) => ({ ...prev, ...patch }));

  const run = useCallback(async (next) => {
    setS(next);
    if (Object.values(next.dim).some((v) => v.trim())) setDimsOpen(true);
    const params = toParams(next);
    const url = new URLSearchParams(params);
    if (next.multi) url.set('multi', '1');
    history.replace({ search: `?${url}` });

    const id = ++requestId.current;
    setBusy(true);
    try {
      const res = await sealLookupApi.search(params);
      if (id !== requestId.current) return;
      setResult({ res, query: next });
      setBanner(null);
      setFieldErrors({});
      setOpenRows({});
    } catch (err) {
      if (id !== requestId.current) return;
      if (err.status === 400) {
        const fields = {};
        const other = [];
        for (const [field, msg] of Object.entries(err.body?.errors || {})) {
          let target = field;
          let tol = false;
          if (/^(inner|outer|height)Tol$/.test(field)) { target = field.slice(0, -3); tol = true; }
          else if (field.startsWith('dimTol.')) { target = `dim.${field.slice(7)}`; tol = true; }
          const text = tol ? `tolerance ${msg}` : msg;
          if (ROLES.includes(target) || target.startsWith('dim.')) fields[target] = fields[target] ? `${fields[target]}; ${text}` : text;
          else other.push(`${field}: ${msg}`);
        }
        setFieldErrors(fields);
        setBanner(other.length ? `Please check: ${other.join(' · ')}` : null);
      } else {
        setFieldErrors({});
        setBanner(err.status
          ? err.body?.error || `Search failed (HTTP ${err.status}).`
          : 'Could not reach the search service. Check the network and try again.');
      }
    } finally {
      if (id === requestId.current) setBusy(false);
    }
  }, [history]);

  const reselect = (base, types, profiles) => run({ ...selectFor(meta, base, types, profiles), page: 1, includeIncomplete: false });

  useEffect(() => {
    let cancelled = false;
    sealLookupApi.meta()
      .then((m) => {
        if (cancelled) return;
        setMeta(m);
        const fromUrl = readUrl(initialSearch.current, m);
        run(selectFor(m, fromUrl, fromUrl.types, fromUrl.profiles));
      })
      .catch(() => { if (!cancelled) setMetaFailed(true); });
    return () => { cancelled = true; };
  }, [run]);

  const header = <PageHeader title="Seal Lookup" description="Find a seal by its own size, the housing it fits, a keyword or its type." />;

  if (metaFailed || !meta) {
    return (
      <UserLayout>
        <div className="space-y-4">
          {header}
          {metaFailed ? (
            <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
              Could not load the seal types. Reload the page; if it keeps failing, the search service is down.
            </div>
          ) : <SkeletonRows rows={6} />}
        </div>
      </UserLayout>
    );
  }

  const labels = labelsFor(meta, s);
  const moreDims = moreDimsFor(meta, s.types, s.profiles);
  const typeCounts = result && Object.fromEntries(result.res.facets.sealTypes.map((t) => [t.key, t.count]));
  const profileCounts = Object.fromEntries((result?.res.facets.profiles || []).map((p) => [p.id, p.count]));
  const chips = meta.profiles.filter((p) => s.types.includes(p.type) && (profileCounts[p.id] > 0 || s.profiles.includes(p.id)));
  const families = [...new Set(meta.sealTypes.map((t) => t.family))];
  const dd = dropdownsFor(meta, s.types);
  const mixedOption = (value) => (value === MIXED ? [[MIXED, 'Multiple']] : []);

  const clickType = (key) => {
    const types = s.multi
      ? (s.types.includes(key) ? s.types.filter((t) => t !== key) : [...s.types, key])
      : (s.types.length === 1 && s.types[0] === key ? [] : [key]);
    reselect(s, types, s.profiles);
  };
  const toggleMulti = (multi) => {
    if (multi || s.types.length <= 1) set({ multi });
    else reselect({ ...s, multi }, s.types.slice(0, 1), s.profiles);
  };
  const pickTypes = (types) => reselect({ ...s, multi: s.multi || types.length > 1 }, types, s.profiles);
  const clickProfile = (id) =>
    reselect(s, s.types, s.profiles.includes(id) ? s.profiles.filter((p) => p !== id) : [...s.profiles, id]);

  const retryWithTolerance = (query) => {
    const tolFill = query.units === 'in' ? '0.005' : '0.1';
    const next = { ...query, dimTol: { ...query.dimTol } };
    ROLES.forEach((r) => { if (next[r].trim() && !next[`${r}Tol`].trim()) next[`${r}Tol`] = tolFill; });
    Object.keys(next.dim).forEach((k) => { if (next.dim[k].trim() && !next.dimTol[k]?.trim()) next.dimTol[k] = tolFill; });
    run({ ...next, page: 1 });
  };
  const goPage = (query, page) => {
    run({ ...query, page });
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <UserLayout>
      <div className="space-y-4">
        {header}

        {banner && (
          <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">{banner}</div>
        )}

        <form
          className="rounded-lg border border-gray-200 bg-brand/10 p-4 shadow-sm"
          onSubmit={(e) => { e.preventDefault(); run({ ...s, page: 1, includeIncomplete: false }); }}
        >
          <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
            <Select id="seal-family" label="Search by" value={dd.family}
              options={[['', 'All'], ...families.map((f) => [f, f]), ...mixedOption(dd.family)]}
              onChange={(f) => pickTypes(meta.sealTypes.filter((t) => t.family === f).map((t) => t.key))} />
            <Select id="seal-type" label="Type" value={dd.type}
              options={[['', 'All'], ...(dd.inFamily.length ? dd.inFamily : meta.sealTypes).map((t) => [t.key, t.name]), ...mixedOption(dd.type)]}
              onChange={(v) => pickTypes(v ? [v] : dd.inFamily.map((t) => t.key))} />
            <Segmented label="Size type" name="seal-mode" value={s.mode}
              options={[['seal', 'Seal size'], ['housing', 'Housing size']]} onChange={(mode) => set({ mode })} />
            {s.mode === 'housing' && (
              <Select id="seal-app" label="Application" value={s.app}
                options={[['any', 'Any'], ['rod', 'Rod'], ['piston', 'Piston']]} onChange={(app) => set({ app })} />
            )}
            <Select id="seal-system" label="Measurement" value={s.system}
              options={[['both', 'Both'], ['metric', 'Metric'], ['imperial', 'Imperial']]} onChange={(system) => set({ system })} />
            <Segmented label="Units" name="seal-units" value={s.units}
              options={[['mm', 'mm'], ['in', 'in']]} onChange={(units) => set({ units })} />
          </div>

          <div className="mt-3">
            <label htmlFor="seal-q" className={LABEL}>Keyword</label>
            <input id="seal-q" type="search" className={`${INPUT} w-full`} placeholder="Part no, profile, material…"
              value={s.q} onChange={(e) => set({ q: e.target.value })} />
          </div>

          <div className="mt-3 flex flex-wrap items-start gap-x-4 gap-y-3">
            {ROLES.map((r) => (
              <SizeField key={r} id={`seal-${r}`} label={labels[r]} value={s[r]} tol={s[`${r}Tol`]} error={fieldErrors[r]}
                onValue={(v) => set({ [r]: v })} onTol={(v) => set({ [`${r}Tol`]: v })} />
            ))}
            <div className="flex gap-2 self-end sm:ml-auto">
              <Button type="submit">Search</Button>
              <Button variant="secondary" onClick={() => run(DEFAULTS)}>Clear</Button>
            </div>
          </div>

          {moreDims.length > 0 && (
            <details className="mt-3" open={dimsOpen} onToggle={(e) => setDimsOpen(e.currentTarget.open)}>
              <summary className="cursor-pointer text-sm font-semibold text-gray-900">More dimensions</summary>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-3">
                {moreDims.map((d) => (
                  <SizeField key={d.key} id={`seal-dim-${d.key}`} label={d.title}
                    value={s.dim[d.key] ?? ''} tol={s.dimTol[d.key] ?? ''} error={fieldErrors[`dim.${d.key}`]}
                    onValue={(v) => set({ dim: { ...s.dim, [d.key]: v } })}
                    onTol={(v) => set({ dimTol: { ...s.dimTol, [d.key]: v } })} />
                ))}
              </div>
            </details>
          )}
        </form>

        <Card padding="sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={s.multi} onChange={(e) => toggleMulti(e.target.checked)}
                className="rounded border-gray-300 text-brand-dark focus:ring-brand" />
              Multiple selection
            </label>
            <div className="flex gap-4">
              <LinkButton onClick={() => reselect({ ...s, multi: true }, meta.sealTypes.map((t) => t.key), s.profiles)}>Select all</LinkButton>
              <LinkButton onClick={() => reselect(s, [], [])}>Deselect all</LinkButton>
            </div>
          </div>

          <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2.5">
            {meta.sealTypes.map((t, i) => {
              const selected = s.types.includes(t.key);
              const count = typeCounts?.[t.key];
              return (
                <React.Fragment key={t.key}>
                  {(i === 0 || meta.sealTypes[i - 1].family !== t.family) && (
                    <h3 className="col-span-full mt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{t.family}</h3>
                  )}
                  <button
                    type="button"
                    aria-pressed={selected}
                    onClick={() => clickType(t.key)}
                    className={`flex flex-col items-center gap-1 rounded-lg border-2 px-2 py-2.5 text-center transition-colors
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2
                      ${selected ? 'border-brand bg-brand/10' : 'border-gray-200 bg-white hover:border-gray-300'}
                      ${count === 0 ? 'opacity-[.55]' : ''}`}
                  >
                    {t.image ? (
                      <img src={t.image} alt="" loading="lazy" className="h-[110px] w-[110px] object-contain" />
                    ) : (
                      <span aria-hidden="true" className="flex h-[110px] w-[110px] items-center justify-center rounded-full bg-brand/20 text-[22px] font-bold text-brand-dark">
                        {initials(t.name)}
                      </span>
                    )}
                    <span className="text-[13px] font-semibold text-gray-900">{t.name}</span>
                    {count != null && <span className="text-xs text-gray-500">{count}<span className="sr-only"> parts</span></span>}
                  </button>
                </React.Fragment>
              );
            })}
          </div>

          {s.types.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold text-gray-700">Profiles in selection:</span>
              {chips.length ? chips.map((p) => {
                const on = s.profiles.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={on}
                    title={p.title || undefined}
                    onClick={() => clickProfile(p.id)}
                    className={`rounded-full border px-2.5 py-0.5 text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand
                      ${on ? 'border-brand-dark bg-brand-dark text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    {p.code} ({profileCounts[p.id] || 0})
                  </button>
                );
              }) : <span className="text-gray-500">none match the current filters</span>}
            </div>
          )}
        </Card>

        <section ref={resultsRef} aria-live="polite" aria-busy={busy} className={`transition-opacity ${busy ? 'opacity-60' : ''}`}>
          <Card padding="sm">
            {!result ? <SkeletonRows rows={5} /> : (() => {
              const { res, query } = result;
              const total = res.mhs.total;
              const ranked = hasSizes(query);
              const pages = Math.ceil(Math.min(total, ranked ? 1000 : 10000) / PAGE_SIZE);
              const leftOutRoles = ROLES.filter((r) => res.leftOut.byRole[r]).map((r) => ROLE_NAMES[r]).join(' / ');
              return (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                    <p>
                      <span className="font-semibold">{total.toLocaleString()} {total === 1 ? 'part' : 'parts'}</span>
                      {!query.includeIncomplete && res.leftOut.total > 0 && (
                        <> · {res.leftOut.total} left out (no {leftOutRoles} in catalogue){' '}
                          <LinkButton onClick={() => run({ ...query, includeIncomplete: true, page: 1 })}>show them</LinkButton></>
                      )}
                      {query.includeIncomplete && (
                        <> · including parts with missing sizes{' '}
                          <LinkButton onClick={() => run({ ...query, includeIncomplete: false, page: 1 })}>hide them</LinkButton></>
                      )}
                    </p>
                    <span className="text-gray-500">
                      {ranked ? 'sorted by best fit' : query.q.trim() ? 'sorted by relevance' : 'sorted by part number'}
                    </span>
                  </div>

                  {res.mhs.truncated && (
                    <Warn>Over 1,000 matches: showing the best fits among the first 1,000 by part number. Narrow the search for a complete ranking.</Warn>
                  )}
                  {!ranked && total > 10000 && (
                    <Warn>Showing the first 10,000 results. Narrow the search (keyword, seal type or sizes) to reach the rest.</Warn>
                  )}
                  {ranked && total === 0 && !hasTolerance(query) && (
                    <Warn>
                      No exact match. Rounded values (e.g. 22.2 mm for a 7/8″ rod) need a tolerance.{' '}
                      <LinkButton onClick={() => retryWithTolerance(query)}>
                        Search again with ± {query.units === 'in' ? '0.005 in' : '0.1 mm'}
                      </LinkButton>
                    </Warn>
                  )}

                  {res.mhs.hits.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                            <th className={TH}>Part no</th>
                            <th className={TH}>Profile</th>
                            <th className={TH}>Seal type</th>
                            <th className={TH}>Sizes</th>
                            <th className={TH}>Material</th>
                            <th className={`${TH} text-right whitespace-nowrap`}>Temp °C</th>
                            <th className={`${TH} text-right whitespace-nowrap`}>Max bar</th>
                            <th className={`${TH} whitespace-nowrap`}>Price &amp; stock</th>
                            <th className={TH}><span className="sr-only">Details</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          {res.mhs.hits.map((hit) => {
                            const open = !!openRows[hit.code];
                            return (
                              <React.Fragment key={hit.code}>
                                <tr className="border-b border-gray-200 align-top">
                                  <td className={`${TD} whitespace-nowrap font-semibold text-gray-900`}>{hit.code}</td>
                                  <td className={TD} title={hit.profile.title || undefined}>{hit.profile.code}</td>
                                  <td className={TD}>{hit.sealType}</td>
                                  <td className={TD}><SizeLines hit={hit} query={query} /></td>
                                  <td className={TD}>{hit.material}</td>
                                  <td className={`${TD} whitespace-nowrap text-right`}>
                                    {hit.tempMinC != null && hit.tempMaxC != null ? `${hit.tempMinC} to ${hit.tempMaxC}` : ''}
                                  </td>
                                  <td className={`${TD} whitespace-nowrap text-right`}>{hit.pressureMaxBar ?? ''}</td>
                                  {/* Portal slot: price, stock and add-to-cart for hit.code once seal parts are in the ERP. */}
                                  <td className={TD}>—</td>
                                  <td className={`${TD} whitespace-nowrap`}>
                                    <LinkButton aria-expanded={open} onClick={() => setOpenRows((o) => ({ ...o, [hit.code]: !open }))}>
                                      {open ? '▾ all' : '▸ all'}
                                    </LinkButton>
                                  </td>
                                </tr>
                                {open && (
                                  <tr className="border-b border-gray-200 bg-gray-50">
                                    <td colSpan={9} className="px-3 py-2">
                                      {hit.dims.length ? (
                                        <dl className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
                                          {hit.dims.map((d) => (
                                            <div key={d.key} className="flex gap-2">
                                              <dt className="text-gray-500">{d.title}</dt>
                                              <dd>{dimText(d)}</dd>
                                            </div>
                                          ))}
                                        </dl>
                                      ) : <span className="text-gray-500">No catalogue dimensions</span>}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {pages > 1 && (
                    <nav aria-label="Result pages" className="flex items-center justify-center gap-4 text-sm">
                      <Button variant="secondary" size="sm" disabled={res.mhs.page <= 1} onClick={() => goPage(query, res.mhs.page - 1)}>‹ Prev</Button>
                      <span>Page {res.mhs.page} of {pages}</span>
                      <Button variant="secondary" size="sm" disabled={res.mhs.page >= pages} onClick={() => goPage(query, res.mhs.page + 1)}>Next ›</Button>
                    </nav>
                  )}
                </div>
              );
            })()}
          </Card>
        </section>
      </div>
    </UserLayout>
  );
}

export default SealLookup;
